import { useCallback } from 'react';
import { useMount, useToggle, useUnmount } from '@cutwise/react-utils';

import type { ModalCloseEvent } from '@/common/components/Modal/Modal';
import { AuthStateEvent } from '@/common/constants/auth';
import locationReload from '@/common/helpers/locationReload';

import type { AuthModalProps } from './AuthModal';

export function useAuthModal(props: AuthModalProps) {
  const { onAfterAuth, onAfterClose, onClose, preventReloadOnAuth } = props;

  const [isLoading, startLoading, stopLoading] = useToggle(true);

  const onMessageHandler = useCallback((e: MessageEvent) => {
    switch (e.data.type) {
      case AuthStateEvent.Init:
        stopLoading();

        break;
      case AuthStateEvent.Unload:
      case AuthStateEvent.Process:
        startLoading();

        break;
      case AuthStateEvent.Success:
        if (window.location.origin === e.origin) {
          if (preventReloadOnAuth) {
            onAfterAuth();
          } else {
            locationReload();
          }
        }

        break;
      default:
        break;
    }
  }, []);

  const onAfterCloseHandler = useCallback(
    (e: ModalCloseEvent) => {
      onAfterClose?.();

      onClose?.(e);
    },
    [onClose, onAfterClose],
  );

  useMount(() => {
    window.addEventListener('message', onMessageHandler);
  });

  useUnmount(() => {
    window.removeEventListener('message', onMessageHandler);
  });

  return {
    isLoading,
    onAfterClose: onAfterCloseHandler,
  };
}
